<template>
  <DialogBase>
    <div
      class="dialog-frame"
      :class="[$attrs.class, `dialog-frame-${size}`]"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`${name}_header`"
      @keydown.esc="emit('onClose')"
      tabindex="-1"
      ref="modalRef"
    >

      <!-- Header -->
      <div class="dialog-frame__header">
        <div class="dialog-frame__header-text">
          <div class="dialog-frame__header-title">
            <slot name="title"></slot>
          </div>
          <div class="dialog-frame__header-description">
            <slot name="description"></slot>
          </div>
        </div>

        <Button v-if="closable" @click="emit('onClose')" severity="close">
          <template #iconLeft><IconModalClose /></template>
        </Button>
      </div>

      <!-- Body -->
      <div class="dialog-frame__body">
        <LoadingModal v-if="loading" />
        <template v-if="!loading">
          <slot name="body"></slot>
        </template>
      </div>

      <!-- Footer -->
      <div class="dialog-frame__footer">
        <slot name="footer"></slot>
      </div>

    </div>
  </DialogBase>
</template>

<script setup lang="ts">
import { onMounted, useTemplateRef } from 'vue';

import Button from '@/components/global/Button.vue';
import DialogBase from '@/components/dialogs/DialogBase.vue';
import LoadingModal from '@/components/global/LoadingModal.vue';

import IconModalClose from '@/icons/modal/ModalClose.svg?component';

const emit = defineEmits(['onClose'])

type DialogSize = 'breathe' | 'wide' | 'ultra' | 'wizard';

const props = withDefaults(defineProps<{
  name: string;
  loading?: boolean;
  closable?: boolean;
  size?: DialogSize;
}>(), {
  name: '',
  closable: true,
});

const modalRef = useTemplateRef('modalRef');

onMounted(() => {
  modalRef.value?.focus()
})
</script>

<style lang="scss">
.dialog-frame {
  @include mixins.rounded-border($color: variables.$primitives-color-border-100);
  background: variables.$primitives-color-background-100;
  box-shadow: rgba(0, 0, 0, .1) 0px 20px 25px -5px, rgba(0, 0, 0, .1) 0px 8px 10px -6px;
  color: variables.$primitives-color-tertiary-500;
  display: flex;
  flex-direction: column;
  max-height: 90%;
  transform: scale(1);
  width: calc(100% - 2rem);

  @media screen and (min-width: 768px) {
    width: 450px;
  }

  &.dialog-frame-breathe {
    @media screen and (min-width: 768px) {
      width: 550px;
    }
  }

  &.dialog-frame-wide {
    @media screen and (min-width: 768px) {
      width: 700px;
    }
  }

  &.dialog-frame-ultra {
    @media screen and (min-width: 768px) {
      width: 1024px;
    }
  }

  &.dialog-frame-wizard {
    width: 1024px;
  }
}

.dialog-frame__header {
  @include mixins.flex-center($gap: variables.$sizing-sm, $horiz: space-between);
  @include sizing.padding('xxs', 'sm');
  border-bottom: 1px solid variables.$primitives-color-border-200;
  color: variables.$primitives-color-primary-600;
  width: 100%;


  .dialog-frame__header-text {
    flex-grow: 1;
  }

  .dialog-frame__header-title {
    @include mixins.flex-center($gap: variables.$sizing-xxs, $horiz: flex-start);
    @include typography.font-display($size: xs, $weight: medium);
    width: 100%;

    > svg {
      flex-shrink: 0;
    }
  }

  .dialog-frame__header-description {
    @include typography.font-body($size: sm, $weight: normal);
    color: variables.$primitives-color-tertiary-400;
  }
}

.dialog-frame__body {
  @include sizing.padding('lg');
  overflow-y: auto;
}

.dialog-frame__footer {
  @include mixins.flex-center($gap: variables.$sizing-lg, $horiz: flex-end);
  @include sizing.padding('lg');

  .button--tertiary {
    margin-right: auto;
  }
}
</style>